<template>
  <b-dropdown
    text="Add to Pool"
    variant="outline-secondary"
    size="sm"
    class="add-to-pool-dropdown"
    :disabled="!canManagePools"
    @hide="onDropdownHide"
    @hidden="resetAddForm"
  >
    <template v-if="!addingToPoolId">
      <b-dropdown-item
        v-for="pool in pools"
        :key="pool.id"
        @click="selectPool(pool.id)"
      >
        {{ pool.name }}
      </b-dropdown-item>
      <b-dropdown-divider v-if="pools.length" />
      <b-dropdown-item v-if="!isCreating" @click="startCreating">
        + Create new pool
      </b-dropdown-item>
      <b-dropdown-form v-else @submit.prevent="createAndAdd">
        <b-form-input
          v-model="newPoolName"
          size="sm"
          placeholder="Pool name"
          autofocus
        />
        <div class="d-flex justify-content-end mt-2" style="gap: 0.4rem">
          <b-button size="sm" variant="outline-secondary" @click="cancelCreate">
            Cancel
          </b-button>
          <b-button
            size="sm"
            variant="primary"
            :disabled="!newPoolName || busy"
            @click="createAndAdd"
          >
            Create
          </b-button>
        </div>
      </b-dropdown-form>
    </template>

    <!-- Tags/notes step, shown after picking a pool — matches the
         POST /open-to-work/pools/{id}/candidates body (tags, notes). -->
    <b-dropdown-form v-else @submit.prevent="confirmAdd">
      <p class="small font-weight-bold mb-2">
        Add to "{{ poolName(addingToPoolId) }}"
      </p>
      <b-form-input
        v-model="tagsInput"
        size="sm"
        placeholder="Tags (comma separated)"
        class="mb-2"
        autofocus
      />
      <b-form-textarea
        v-model="notesInput"
        size="sm"
        placeholder="Notes (optional)"
        rows="2"
        class="mb-2"
      />
      <div class="d-flex justify-content-end" style="gap: 0.4rem">
        <b-button size="sm" variant="outline-secondary" @click="resetAddForm">
          Cancel
        </b-button>
        <b-button
          size="sm"
          variant="primary"
          :disabled="busy"
          @click="confirmAdd"
        >
          Add
        </b-button>
      </div>
    </b-dropdown-form>
  </b-dropdown>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'AddToPoolDropdown',

  props: {
    tradieId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      isCreating: false,
      newPoolName: '',
      addingToPoolId: null,
      tagsInput: '',
      notesInput: '',
      busy: false,
    }
  },

  computed: {
    ...mapGetters('candidate', ['pools']),
    ...mapGetters('auth', ['hasPermission']),

    canManagePools() {
      return this.hasPermission('candidates.manage_pools')
    },
  },

  created() {
    if (!this.pools || !this.pools.length) {
      this.fetchPools().catch(() => {})
    }
  },

  methods: {
    ...mapActions('candidate', [
      'fetchPools',
      'createPool',
      'addCandidateToPool',
    ]),

    poolName(poolId) {
      const pool = this.pools.find((p) => p.id === poolId)
      return pool ? pool.name : ''
    },

    // b-dropdown-item closes its parent b-dropdown on click by default
    // (standard Bootstrap dropdown behaviour) — cancel that while we're mid
    // multi-step flow (picking tags/notes, or naming a new pool), otherwise
    // the dropdown closes itself the instant you click "+ Create new pool"
    // or a pool name, before the form ever gets a chance to show.
    onDropdownHide(bvEvent) {
      if (this.isCreating || this.addingToPoolId) {
        bvEvent.preventDefault()
      }
    },

    selectPool(poolId) {
      this.addingToPoolId = poolId
    },

    startCreating() {
      this.isCreating = true
    },

    parsedTags() {
      return this.tagsInput
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
    },

    async confirmAdd() {
      if (this.busy || !this.addingToPoolId) return
      this.busy = true
      try {
        await this.addCandidateToPool({
          poolId: this.addingToPoolId,
          tradieId: this.tradieId,
          tags: this.parsedTags(),
          notes: this.notesInput || null,
        })
        this.$emit('added', this.addingToPoolId)
        this.resetAddForm()
      } catch (err) {
        this.$emit('error', err)
      } finally {
        this.busy = false
      }
    },

    resetAddForm() {
      this.isCreating = false
      this.newPoolName = ''
      this.addingToPoolId = null
      this.tagsInput = ''
      this.notesInput = ''
    },

    cancelCreate() {
      this.isCreating = false
      this.newPoolName = ''
    },

    async createAndAdd() {
      if (!this.newPoolName || this.busy) return
      this.busy = true
      try {
        const pool = await this.createPool(this.newPoolName)
        const poolId = (pool && pool.data && pool.data.id) || (pool && pool.id)
        this.cancelCreate()
        if (poolId) {
          // Let the user tag/note the candidate on this fresh pool too,
          // rather than adding blind.
          this.addingToPoolId = poolId
        }
      } catch (err) {
        this.$emit('error', err)
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.add-to-pool-dropdown /deep/ .dropdown-menu {
  min-width: 240px;
  padding: 0.5rem;
}
</style>
